/**
 * Bradford Factor — the client's half.
 *
 * A deliberate mirror of the same names in
 * `functions/src/staffAbsence/bradfordLogic.ts`. The two workspaces build
 * separately and neither can import the other; `scripts/bradford-test.mjs`
 * imports both copies and fails if they disagree.
 *
 * The client never COMPUTES a score for display — every number on the page
 * comes from getBradfordReport / getStaffMemberAbsence. These helpers exist
 * for the range picker (rolling year, year buttons, the stored window, this
 * week), for naming bands, and for formatting; keep this file free of
 * anything the server half does not also have.
 */

export const STAFF_ABSENCE_YEARS = 5;
export const BRADFORD_DEFAULT_THRESHOLDS = [45, 100, 200, 450];
export const BRADFORD_MAX_THRESHOLDS = 6;
export const CALENDAR_MAX_DAYS = 92;
export const LONG_TERM_MIN_DAYS = 20;
export const NEWLY_CROSSED_LOOKBACK_DAYS = 28;
export const ROLLING_MONTHS = 12;
export const THRESHOLD_LABEL_MAX = 40;

/** Pattern kinds the server can report, in display order — mirrored from
 *  bradfordLogic so a chip is never rendered for a kind the client does not
 *  know how to describe. */
export const PATTERN_KINDS = ['weekday', 'weekendAdjacent', 'holiday', 'annualLeave', 'sameWeek', 'recent', 'selfCert'] as const;
export type PatternKind = typeof PATTERN_KINDS[number];

/** The chip on the table. One place, so a new kind is named once. */
export const PATTERN_LABELS: Record<PatternKind, string> = {
  weekday: 'Same weekday',
  weekendAdjacent: 'Around weekends',
  holiday: 'Around holidays',
  annualLeave: 'Joined to annual leave',
  sameWeek: 'Same week each year',
  recent: 'Recent cluster',
  selfCert: '5-day occasions',
};

/** What each pattern means, for the person page and the setup copy. */
export const PATTERN_EXPLAINERS: Record<PatternKind, string> = {
  weekday: 'Three fifths or more of their occasions begin on the same weekday.',
  weekendAdjacent: 'Three fifths or more of their occasions are short and run up to or on from a weekend.',
  holiday: 'Occasions that begin the first day back after a break, or end the last day before one.',
  annualLeave: 'Sickness that begins the day after annual leave ends, or runs up to the day it begins.',
  sameWeek: 'Off in the same week of the year in three or more years, and that week holds half their absences.',
  recent: 'Three or more occasions in the last eight weeks.',
  selfCert: 'Repeated occasions of exactly five working days — the self-certification limit.',
};

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function londonToday(nowMs: number = Date.now()): string {
  return new Date(nowMs).toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
}

export function isValidDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const m = DATE_RE.exec(value);
  if (!m) return false;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1) return false;
  const dim = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return d <= dim;
}

function toUtc(ymd: string): Date {
  const m = DATE_RE.exec(ymd)!;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

function fromUtc(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function addDays(ymd: string, n: number): string {
  const d = toUtc(ymd);
  d.setUTCDate(d.getUTCDate() + n);
  return fromUtc(d);
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(ymd: string): number {
  return toUtc(ymd).getUTCDay();
}

export function isWeekend(ymd: string): boolean {
  const dow = dayOfWeek(ymd);
  return dow === 0 || dow === 6;
}

export function daysBetween(from: string, to: string): number {
  return Math.round((toUtc(to).getTime() - toUtc(from).getTime()) / 86_400_000) + 1;
}

export function addMonths(ymd: string, n: number): string {
  const m = DATE_RE.exec(ymd)!;
  const y = Number(m[1]), mo = Number(m[2]) - 1 + n, d = Number(m[3]);
  const first = new Date(Date.UTC(y, mo, 1));
  const dim = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return fromUtc(new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), Math.min(d, dim))));
}

export function rollingRange(end: string): { from: string; to: string } {
  return { from: addDays(addMonths(end, -ROLLING_MONTHS), 1), to: end };
}

export function periodBefore(from: string, to: string): { from: string; to: string } {
  const len = daysBetween(from, to);
  const prevTo = addDays(from, -1);
  return { from: addDays(prevTo, -(len - 1)), to: prevTo };
}

export function monthStart(ymd: string): string {
  return `${ymd.slice(0, 7)}-01`;
}

export function monthLabel(ymd: string): string {
  return toUtc(monthStart(ymd)).toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function academicYearStartYear(ymd: string): number {
  const y = Number(ymd.slice(0, 4));
  const m = Number(ymd.slice(5, 7));
  return m >= 9 ? y : y - 1;
}

export function academicYearRange(startYear: number): { from: string; to: string } {
  return { from: `${startYear}-09-01`, to: `${startYear + 1}-08-31` };
}

export function academicYearLabel(startYear: number): string {
  return `${startYear}/${String(startYear + 1).slice(2)}`;
}

export function syncWindowStart(today: string): string {
  return `${academicYearStartYear(today) - (STAFF_ABSENCE_YEARS - 1)}-09-01`;
}

export function weekRange(ymd: string): { from: string; to: string } {
  const dow = dayOfWeek(ymd);
  const monday = addDays(ymd, dow === 0 ? -6 : 1 - dow);
  return { from: monday, to: addDays(monday, 4) };
}

export function bradford(spells: number, days: number): number {
  return Math.round(spells * spells * days * 100) / 100;
}

export function bandFor(score: number, thresholds: number[]): number {
  let band = 0;
  for (const t of thresholds) if (score >= t) band += 1;
  return band;
}

export function normaliseThresholds(v: unknown): number[] | null {
  if (!Array.isArray(v) || v.length === 0 || v.length > BRADFORD_MAX_THRESHOLDS) return null;
  const nums = v.map((x) => (typeof x === 'number' && Number.isFinite(x) ? Math.round(x) : NaN));
  if (nums.some((n) => Number.isNaN(n) || n <= 0)) return null;
  for (let i = 1; i < nums.length; i += 1) if (nums[i]! <= nums[i - 1]!) return null;
  return nums;
}

export function normaliseThresholdLabels(v: unknown, thresholdCount: number): string[] | null {
  if (v === null || v === undefined) return [];
  if (!Array.isArray(v)) return null;
  if (v.length === 0) return [];
  if (v.length !== thresholdCount) return null;
  const out = v.map((x) => (typeof x === 'string' ? x.trim() : null));
  if (out.some((x) => x === null || x.length > THRESHOLD_LABEL_MAX)) return null;
  return out as string[];
}

export function bandLabel(band: number, thresholds: number[], labels: string[] | null | undefined): string {
  if (band === 0) return 'Under';
  const label = labels?.[band - 1];
  return label && label.length > 0 ? label : `Over ${thresholds[band - 1]}`;
}

/** "3 September 2026". */
export function formatDate(ymd: string): string {
  if (!isValidDate(ymd)) return ymd;
  return toUtc(ymd).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** "3 Sep 2026". */
export function formatDateShort(ymd: string): string {
  if (!isValidDate(ymd)) return ymd;
  return toUtc(ymd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** "Wed 3 Sep". */
export function formatDayShort(ymd: string): string {
  if (!isValidDate(ymd)) return ymd;
  return toUtc(ymd).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** "today", "yesterday", "12 days ago", "3 months ago". */
export function daysAgoLabel(ymd: string, today: string): string {
  const n = daysBetween(ymd, today) - 1;
  if (n <= 0) return 'today';
  if (n === 1) return 'yesterday';
  if (n < 60) return `${n} days ago`;
  const months = Math.round(n / 30.4);
  return months < 24 ? `${months} months ago` : `${Math.round(months / 12)} years ago`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Review points — mirrored from functions/src/staffAbsence/reviewPointsLogic.ts.
// The client never computes whether a point was reached; these exist for the
// Setup form, the stage picker and labels.
// ─────────────────────────────────────────────────────────────────────────────

export interface ReviewPointsConfig {
  windowMonths: number;
  occasions: number;
  days: number;
  longTermDays: number;
  returnToWorkDays: number;
  patternIsReviewPoint: boolean;
}

export const REVIEW_POINTS_DEFAULTS: ReviewPointsConfig = {
  windowMonths: 6, occasions: 3, days: 8, longTermDays: 20, returnToWorkDays: 3,
  patternIsReviewPoint: true,
};

export const REVIEW_POINTS_BOUNDS: Record<ReviewPointsNumericKey, [number, number]> = {
  windowMonths: [1, 24], occasions: [1, 20], days: [1, 260], longTermDays: [1, 260], returnToWorkDays: [1, 10],
};

export type ReviewPointsNumericKey =
  'windowMonths' | 'occasions' | 'days' | 'longTermDays' | 'returnToWorkDays';

const REVIEW_POINT_KEYS = Object.keys(REVIEW_POINTS_DEFAULTS) as (keyof ReviewPointsConfig)[];
const REVIEW_POINT_NUMERIC_KEYS = Object.keys(REVIEW_POINTS_BOUNDS) as ReviewPointsNumericKey[];

export function normaliseReviewPoints(v: unknown): ReviewPointsConfig | null {
  if (v === null || v === undefined) return null;
  if (typeof v !== 'object' || Array.isArray(v)) return null;
  const obj = v as Record<string, unknown>;
  for (const k of Object.keys(obj)) if (!REVIEW_POINT_KEYS.includes(k as keyof ReviewPointsConfig)) return null;
  const out = { ...REVIEW_POINTS_DEFAULTS };
  for (const k of REVIEW_POINT_NUMERIC_KEYS) {
    if (obj[k] === undefined) continue;
    const n = obj[k];
    if (typeof n !== 'number' || !Number.isFinite(n)) return null;
    const r = Math.round(n);
    const [lo, hi] = REVIEW_POINTS_BOUNDS[k];
    if (r < lo || r > hi) return null;
    out[k] = r;
  }
  if (obj['patternIsReviewPoint'] !== undefined) {
    if (typeof obj['patternIsReviewPoint'] !== 'boolean') return null;
    out.patternIsReviewPoint = obj['patternIsReviewPoint'];
  }
  return out;
}

export const STAGE_TRIGGERS = {
  stage1: { intermittentDays: 2, longTermDays: 5 },
  stage2: { intermittentOccasions: 1, longTermDays: 5 },
} as const;

export const CASE_STAGES = ['informal', 'stage1', 'stage2', 'final'] as const;
export type CaseStage = typeof CASE_STAGES[number];
export const CASE_TRACKS = ['intermittent', 'longTerm'] as const;
export type CaseTrack = typeof CASE_TRACKS[number];

export const STAGE_LABELS: Record<CaseStage, string> = {
  informal: 'Informal conversation',
  stage1: 'Stage 1 letter',
  stage2: 'Stage 2 letter',
  final: 'Final absence review',
};

export const TRACK_LABELS: Record<CaseTrack, string> = {
  intermittent: 'Persistent intermittent absence',
  longTerm: 'Long-term absence',
};

export const MEASURES = ['bradford', 'reviewPoints'] as const;
export type StaffAbsenceMeasure = typeof MEASURES[number];

export function canAdvanceTo(history: readonly { stage: CaseStage }[], next: CaseStage): boolean {
  if (history.length === 0) return next === 'informal' || next === 'stage1';
  const last = history[history.length - 1]!.stage;
  return CASE_STAGES.indexOf(next) === CASE_STAGES.indexOf(last) + 1;
}

export function nextWorkingDay(ymd: string): string {
  let d = ymd;
  while (isWeekend(d)) d = addDays(d, 1);
  return d;
}

export function addWorkingDays(ymd: string, n: number): string {
  let d = ymd;
  for (let i = 0; i < n; i += 1) {
    d = addDays(d, 1);
    while (isWeekend(d)) d = addDays(d, 1);
  }
  return d;
}
